import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

// A small geometry kit for building models in code (from the superposition spike's weapon experiment, where the code
// route matched headless Blender): every part becomes non-indexed position + normal + colour, so a whole model merges
// into one vertex-coloured mesh (one draw call, one outline hull that still rings every part). `Soup` and `loft` are
// Blender's lofted rings, per-face painting, bisect and auto smooth, in three.js.

export type Col = THREE.ColorRepresentation;
/** A three.js colour from any representation. */
export const C = (c: Col): THREE.Color => new THREE.Color(c);

/** Strips everything but position and normal, goes non-indexed and paints the vertices (one colour or per vertex). */
export function paint(geo: THREE.BufferGeometry, c: Col | ((p: THREE.Vector3, i: number) => THREE.Color)): THREE.BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (!g.getAttribute("normal")) g.computeVertexNormals();
  for (const k of Object.keys(g.attributes)) if (k !== "position" && k !== "normal") g.deleteAttribute(k);
  const P = g.getAttribute("position");
  const out = new Float32Array(P.count * 3);
  const fixed = typeof c === "function" ? null : C(c);
  const p = new THREE.Vector3();
  for (let i = 0; i < P.count; i++) {
    const k = fixed ?? (c as (p: THREE.Vector3, i: number) => THREE.Color)(p.fromBufferAttribute(P, i), i);
    out[i * 3] = k.r;
    out[i * 3 + 1] = k.g;
    out[i * 3 + 2] = k.b;
  }
  g.setAttribute("color", new THREE.BufferAttribute(out, 3));
  return g;
}

/** Indexed geometry that already carries a colour attribute: keep position, normal, colour; go non-indexed. */
export function keep(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  for (const k of Object.keys(geo.attributes)) if (k !== "position" && k !== "normal" && k !== "color") geo.deleteAttribute(k);
  return geo.index ? geo.toNonIndexed() : geo;
}

/** Merges parts that share the same attributes (position, normal, colour) into one geometry. */
export function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const g = mergeGeometries(parts, false);
  if (!g) throw new Error("@rpg/meshkit: merge failed (attribute mismatch)");
  return g;
}

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
/** Moves a geometry: position, Euler rotation (XYZ), scale. */
export function at(geo: THREE.BufferGeometry, pos: [number, number, number], rot: [number, number, number] = [0, 0, 0], scale: [number, number, number] = [1, 1, 1]): THREE.BufferGeometry {
  _q.setFromEuler(_e.set(...rot));
  geo.applyMatrix4(_m.compose(new THREE.Vector3(...pos), _q, new THREE.Vector3(...scale)));
  return geo;
}

/** Moves a geometry so its +Y axis points along `n`, then to `pos`. */
export function alongNormal(geo: THREE.BufferGeometry, pos: THREE.Vector3, n: THREE.Vector3): THREE.BufferGeometry {
  _q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), n.clone().normalize());
  geo.applyMatrix4(_m.compose(pos, _q, new THREE.Vector3(1, 1, 1)));
  return geo;
}

/** Flat-shaded triangle soup with per-triangle colours. */
export class Tris {
  private p: number[] = [];
  private c: number[] = [];
  tri(a: THREE.Vector3, b: THREE.Vector3, d: THREE.Vector3, col: THREE.Color): void {
    this.p.push(a.x, a.y, a.z, b.x, b.y, b.z, d.x, d.y, d.z);
    for (let i = 0; i < 3; i++) this.c.push(col.r, col.g, col.b);
  }
  geometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.p, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.c, 3));
    g.computeVertexNormals();
    return g;
  }
}

/**
 * A closed-around grid surface (rows along v, columns wrap around u), indexed so normals smooth across columns, then
 * returned non-indexed. `fn(u, v)` gives the point and its colour; `normal` overrides the computed normals.
 */
export function grid(
  nu: number,
  nv: number,
  fn: (u: number, v: number) => { p: THREE.Vector3; c: THREE.Color; n?: THREE.Vector3 },
): THREE.BufferGeometry {
  const pos: number[] = [];
  const col: number[] = [];
  const nor: number[] = [];
  let given = true;
  for (let j = 0; j <= nv; j++)
    for (let i = 0; i < nu; i++) {
      const r = fn(i / nu, j / nv);
      pos.push(r.p.x, r.p.y, r.p.z);
      col.push(r.c.r, r.c.g, r.c.b);
      if (r.n) nor.push(r.n.x, r.n.y, r.n.z);
      else given = false;
    }
  const idx: number[] = [];
  for (let j = 0; j < nv; j++)
    for (let i = 0; i < nu; i++) {
      const a = j * nu + i;
      const b = j * nu + ((i + 1) % nu);
      const c = (j + 1) * nu + i;
      const d = (j + 1) * nu + ((i + 1) % nu);
      idx.push(a, d, b, a, c, d);
    }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  if (given) g.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  else g.computeVertexNormals();
  return g.toNonIndexed();
}

/** A five-point star outline (10 points, the first point up at +Y) of outer radius `ro`, inner `ro * k`. */
export function starPoints(ro: number, k: number): THREE.Vector2[] {
  const out: THREE.Vector2[] = [];
  for (let i = 0; i < 10; i++) {
    const a = Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? ro : ro * k;
    out.push(new THREE.Vector2(Math.cos(a) * r, Math.sin(a) * r));
  }
  return out;
}

// --- triangle soup with per-face colour (the gladius: Blender's lofted rings, face painting and bisect, in three.js) --

type V3 = THREE.Vector3;
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _c = new THREE.Vector3();

/**
 * Triangles with one colour and one tag each (the tag names what a face is, e.g. a blade strip, so a repaint can
 * still tell after a cut). `bisect` splits faces along any plane, like Blender's bisect_plane, so a painted
 * boundary can run at any angle and stay crisp; `geometry` smooths normals only across edges whose faces meet
 * under an angle (Blender's auto smooth), so creases stay hard.
 */
export class Soup {
  p: number[] = [];
  c: number[] = [];
  t: number[] = [];
  tri(a: V3, b: V3, d: V3, col: THREE.Color, tag = 0): void {
    this.p.push(a.x, a.y, a.z, b.x, b.y, b.z, d.x, d.y, d.z);
    this.c.push(col.r, col.g, col.b);
    this.t.push(tag);
  }
  get count(): number {
    return this.t.length;
  }
  /** Cuts every face that the plane n·p = d crosses (and `only` accepts, by centroid) into faces wholly on one
   *  side, winding kept. */
  bisect(n: V3, d: number, only?: (centre: V3) => boolean, eps = 1e-9): void {
    const P = this.p;
    const out: number[] = [];
    const oc: number[] = [];
    const ot: number[] = [];
    const v = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
    const s = [0, 0, 0];
    const push = (col: number, tag: number, ...vs: V3[]): void => {
      for (const x of vs) out.push(x.x, x.y, x.z);
      oc.push(this.c[col * 3]!, this.c[col * 3 + 1]!, this.c[col * 3 + 2]!);
      ot.push(tag);
    };
    const cut = (a: V3, sa: number, b: V3, sb: number): V3 => a.clone().lerp(b, sa / (sa - sb));
    for (let f = 0; f < this.t.length; f++) {
      let pos = 0;
      let neg = 0;
      for (let k = 0; k < 3; k++) {
        v[k]!.fromArray(P, f * 9 + k * 3);
        s[k] = n.dot(v[k]!) - d;
        if (s[k]! > eps) pos++;
        else if (s[k]! < -eps) neg++;
      }
      const tag = this.t[f]!;
      if (pos && neg && only && !only(_a.copy(v[0]!).add(v[1]!).add(v[2]!).multiplyScalar(1 / 3))) pos = 0;
      if (!pos || !neg) {
        push(f, tag, v[0]!, v[1]!, v[2]!);
        continue;
      }
      // rotate so vertex 0 is the lone one (or the one on the plane)
      let k0 = 0;
      const sign = (x: number): number => (x > eps ? 1 : x < -eps ? -1 : 0);
      for (let k = 0; k < 3; k++) {
        const a = sign(s[k]!);
        const b = sign(s[(k + 1) % 3]!);
        const c = sign(s[(k + 2) % 3]!);
        if (a === 0 || (a !== b && a !== c && b === c)) k0 = k;
      }
      const A = v[k0]!;
      const B = v[(k0 + 1) % 3]!;
      const Cc = v[(k0 + 2) % 3]!;
      const sA = s[k0]!;
      const sB = s[(k0 + 1) % 3]!;
      const sC = s[(k0 + 2) % 3]!;
      if (sign(sA) === 0) {
        const Q = cut(B, sB, Cc, sC);
        push(f, tag, A, B, Q);
        push(f, tag, A, Q, Cc);
      } else {
        const Pp = cut(A, sA, B, sB);
        const Q = cut(A, sA, Cc, sC);
        push(f, tag, A, Pp, Q);
        push(f, tag, Pp, B, Cc);
        push(f, tag, Pp, Cc, Q);
      }
    }
    this.p = out;
    this.c = oc;
    this.t = ot;
  }
  /** Repaints every face from its centroid, its face normal and its tag. */
  repaint(fn: (centre: V3, n: V3, tag: number) => THREE.Color): void {
    const cen = new THREE.Vector3();
    const nrm = new THREE.Vector3();
    for (let f = 0; f < this.t.length; f++) {
      _a.fromArray(this.p, f * 9);
      _b.fromArray(this.p, f * 9 + 3);
      _c.fromArray(this.p, f * 9 + 6);
      cen.copy(_a).add(_b).add(_c).multiplyScalar(1 / 3);
      nrm.subVectors(_b, _a).cross(_c.clone().sub(_a)).normalize();
      const k = fn(cen, nrm, this.t[f]!);
      this.c[f * 3] = k.r;
      this.c[f * 3 + 1] = k.g;
      this.c[f * 3 + 2] = k.b;
    }
  }
  /** Non-indexed position + normal + colour; normals smoothed across faces that meet under `deg` degrees. */
  geometry(deg: number): THREE.BufferGeometry {
    const F = this.t.length;
    const fn = new Float32Array(F * 3); // area-weighted face normals
    const fu = new Float32Array(F * 3); // unit face normals
    for (let f = 0; f < F; f++) {
      _a.fromArray(this.p, f * 9);
      _b.fromArray(this.p, f * 9 + 3);
      _c.fromArray(this.p, f * 9 + 6);
      _b.sub(_a);
      _c.sub(_a);
      _b.cross(_c);
      fn.set([_b.x, _b.y, _b.z], f * 3);
      const l = _b.length() || 1;
      fu.set([_b.x / l, _b.y / l, _b.z / l], f * 3);
    }
    const key = (i: number): string => `${Math.round(this.p[i]! * 2e5)},${Math.round(this.p[i + 1]! * 2e5)},${Math.round(this.p[i + 2]! * 2e5)}`;
    const at = new Map<string, number[]>();
    for (let f = 0; f < F; f++)
      for (let k = 0; k < 3; k++) {
        const kk = key(f * 9 + k * 3);
        let l = at.get(kk);
        if (!l) at.set(kk, (l = []));
        l.push(f);
      }
    const cos = Math.cos((deg * Math.PI) / 180);
    const nor = new Float32Array(F * 9);
    const col = new Float32Array(F * 9);
    const sum = new THREE.Vector3();
    for (let f = 0; f < F; f++)
      for (let k = 0; k < 3; k++) {
        sum.set(0, 0, 0);
        for (const g of at.get(key(f * 9 + k * 3))!) {
          const d = fu[f * 3]! * fu[g * 3]! + fu[f * 3 + 1]! * fu[g * 3 + 1]! + fu[f * 3 + 2]! * fu[g * 3 + 2]!;
          if (g === f || d >= cos) sum.x += fn[g * 3]!, sum.y += fn[g * 3 + 1]!, sum.z += fn[g * 3 + 2]!;
        }
        if (sum.lengthSq() < 1e-30) sum.set(fu[f * 3]!, fu[f * 3 + 1]!, fu[f * 3 + 2]!);
        sum.normalize();
        nor.set([sum.x, sum.y, sum.z], f * 9 + k * 3);
        col.set([this.c[f * 3]!, this.c[f * 3 + 1]!, this.c[f * 3 + 2]!], f * 9 + k * 3);
      }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.p, 3));
    g.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
    g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    return g;
  }
}

/** What a loft's painter is told about one face: its height, where the viewer of that side sees it across the part
 *  (u: -1 left .. 1 right, mirrored on the back so both faces read the same), which side, its normal. */
export interface Face {
  y: number;
  u: number;
  front: boolean;
  n: V3;
}

/**
 * Joins rings of points bottom to top (every ring the same count, running from +X toward +Z), painting each quad
 * from its centre: `u` comes from the column's mid angle, so painted boundaries land exactly on columns and rows.
 * `lo` / `hi` close an end with a fan: true for a flat cap at the ring's height, a number for a pole at that y.
 */
export function loft(
  s: Soup,
  rings: V3[][],
  paint: (f: Face) => THREE.Color,
  opt: { lo?: boolean | number; hi?: boolean | number; tag?: (col: number) => number } = {},
): void {
  const seg = rings[0]!.length;
  const face = (a: V3, b: V3, d: V3, col: number, y: number): void => {
    const th = (2 * Math.PI * (col + 0.5)) / seg;
    const front = Math.sin(th) >= 0;
    const n = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(d, a)).normalize();
    s.tri(a, b, d, paint({ y, u: front ? Math.cos(th) : -Math.cos(th), front, n }), opt.tag?.(col) ?? 0);
  };
  for (let j = 0; j < rings.length - 1; j++) {
    const A = rings[j]!;
    const B = rings[j + 1]!;
    const y = (A[0]!.y + B[0]!.y) / 2;
    for (let k = 0; k < seg; k++) {
      const k1 = (k + 1) % seg;
      // one paint per quad (both halves the same colour)
      const th = (2 * Math.PI * (k + 0.5)) / seg;
      const front = Math.sin(th) >= 0;
      const n = new THREE.Vector3().subVectors(B[k]!, A[k]!).cross(new THREE.Vector3().subVectors(A[k1]!, A[k]!)).normalize();
      if (n.lengthSq() < 0.5) n.subVectors(B[k1]!, A[k]!).cross(new THREE.Vector3().subVectors(A[k1]!, A[k]!)).normalize();
      const col = paint({ y, u: front ? Math.cos(th) : -Math.cos(th), front, n });
      const tag = opt.tag?.(k) ?? 0;
      s.tri(A[k]!, B[k]!, B[k1]!, col, tag);
      s.tri(A[k]!, B[k1]!, A[k1]!, col, tag);
    }
  }
  const cap = (ring: V3[], end: boolean | number | undefined, up: boolean): void => {
    if (end === undefined || end === false) return;
    const y = ring[0]!.y;
    const c = new THREE.Vector3(0, typeof end === "number" ? end : y, 0);
    for (let k = 0; k < seg; k++) {
      const k1 = (k + 1) % seg;
      if (up) face(c, ring[k1]!, ring[k]!, k, y);
      else face(c, ring[k]!, ring[k1]!, k, y);
    }
  };
  cap(rings[0]!, opt.lo, false);
  cap(rings[rings.length - 1]!, opt.hi, true);
}

/** An elliptical ring at height y (seg points from +X toward +Z). */
export function ring(y: number, rx: number, rz: number, seg: number): V3[] {
  const out: V3[] = [];
  for (let k = 0; k < seg; k++) {
    const th = (2 * Math.PI * k) / seg;
    out.push(new THREE.Vector3(rx * Math.cos(th), y, rz * Math.sin(th)));
  }
  return out;
}

/** Sorted heights with near-duplicates (closer than `gap`) dropped, the earlier-listed (exact) values kept. */
export function rows(exact: number[], fill: number[], gap = 0.0003): number[] {
  const out = [...exact];
  for (const y of fill) if (out.every((e) => Math.abs(e - y) > gap)) out.push(y);
  return out.sort((a, b) => a - b);
}
